import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { useCategoryTree, nodeName } from '../../categoryTree.js';
import { countryOptions } from '../../countries.js';
import { UNITS, INCOTERMS } from '../../productOptions.js';
import { Alert, Field } from '../../components/ui.jsx';
import RichTextEditor from '../../components/RichTextEditor.jsx';
import SpecFields, { specsToBody } from '../member/wizard/SpecFields.jsx';
import { AccountHead } from '../account/AccountLayout.jsx';
import { unitLabel, inDays } from './requestUtils.js';
import './requests.css';

// My space › "Publier un besoin / Post a need" (and edit). Category at any
// level of the catalogue tree, the same spec chips as products (never
// required here), FR or EN (auto-translated once saved), photos / PDFs,
// validity, visibility (company name or anonymous). Goes to PME's review.
const PAYMENT = ['tt', 'lc', 'cad', 'escrow'];
const CURRENCIES = ['USD', 'CAD', 'EUR'];
const CERTS = ['ISO 9001', 'ISO 14001', 'HACCP', 'Halal', 'Kosher', 'Organic', 'CE', 'SGS', 'Mill test certificate', 'FDA', 'REACH'];
const empty = (lang) => ({
  title: '', categoryId: '', descriptionHtml: '', quantity: '', unit: 'tonne', targetPrice: '', priceOnRequest: true, currency: 'USD', incoterm: '',
  destCountry: '', destPort: '', deliveryBy: '', timeframe: '', paymentTerms: [], certifications: [], expiresOn: inDays(30), anonymous: false, lang, specValues: {},
});

// Catalogue picker: search the whole tree or open it level by level; any
// level can be chosen ("Use “Metals”").
function CategoryPicker({ tree, value, onPick }) {
  const { t, lang } = useI18n();
  const [at, setAt] = useState(null);
  const [q, setQ] = useState('');
  const hits = useMemo(() => (q.trim().length >= 2 ? tree.search(q, lang, 12) : []), [q, tree, lang]);
  const crumbs = at ? tree.path(at) : [];
  const level = tree.children(at);
  const current = at ? tree.get(at) : null;
  return (
    <div className="wz-cat">
      <div className="wz-search">
        <label htmlFor="rq-cat-q" className="field-label">{t('wizard.searchLabel')}</label>
        <input id="rq-cat-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('wizard.searchPlaceholder')} autoComplete="off" />
        {q.trim().length >= 2 && (
          <ul className="wz-results">
            {hits.map((h) => (
              <li key={h.node.id}><button type="button" onClick={() => { setQ(''); onPick(h.node.id); }}>
                <span>{h.path.map((n, j) => <React.Fragment key={n.id}>{j > 0 && <i aria-hidden="true"> › </i>}{j === h.path.length - 1 ? <strong>{nodeName(n, lang)}</strong> : nodeName(n, lang)}</React.Fragment>)}</span>
              </button></li>
            ))}
            {!hits.length && <li className="wz-noresult muted">{t('wizard.noMatch')}</li>}
          </ul>
        )}
      </div>
      <nav className="wz-crumbs" aria-label={t('wizard.breadcrumb')}>
        <button type="button" onClick={() => setAt(null)} aria-current={!at ? 'page' : undefined}>{t('wizard.allSectors')}</button>
        {crumbs.map((n) => <React.Fragment key={n.id}><span aria-hidden="true">›</span><button type="button" onClick={() => setAt(n.id)}>{nodeName(n, lang)}</button></React.Fragment>)}
      </nav>
      {current && <p><button type="button" className="btn btn-primary btn-sm" onClick={() => onPick(current.id)}>{t('buying.f.useThisLevel', { name: nodeName(current, lang) })}</button></p>}
      <div className="chip-choice">
        {level.map((n) => (
          <button key={n.id} type="button" className={`chip-toggle${String(n.id) === String(value) ? ' on' : ''}`}
            onClick={() => (tree.children(n.id).length ? setAt(n.id) : onPick(n.id))}>
            {nodeName(n, lang)}{tree.children(n.id).length ? ' ›' : ''}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function RequestForm() {
  const { id } = useParams();
  const { t, lang, errorText } = useI18n();
  const navigate = useNavigate();
  const tree = useCategoryTree();
  usePageTitle(id ? 'buying.editTitle' : 'buying.newTitle');
  const countries = useMemo(() => countryOptions(lang), [lang]);
  const [f, setF] = useState(() => empty(lang));
  const [existing, setExisting] = useState(null);
  const [fields, setFields] = useState([]);
  const [picking, setPicking] = useState(!id);
  const [files, setFiles] = useState([]);
  const [usage, setUsage] = useState(null);
  const [certText, setCertText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) { api('/my/buying-requests/usage').then(setUsage).catch(() => {}); return; }
    api(`/my/buying-requests/${id}`).then((r) => {
      setExisting(r);
      const specValues = {};
      for (const s of r.specs) (specValues[s.groupId] ||= []).push(s.value);
      setF({
        title: r.original?.title ?? r.title, categoryId: r.category ? String(r.category.id) : '', descriptionHtml: r.original?.descriptionHtml ?? r.descriptionHtml,
        quantity: r.quantity ?? '', unit: r.unit || '', targetPrice: r.targetPrice ?? '', priceOnRequest: r.priceOnRequest, currency: r.currency, incoterm: r.incoterm || '',
        destCountry: r.destCountry || '', destPort: r.destPort || '', deliveryBy: r.deliveryBy || '', timeframe: r.timeframe || '', paymentTerms: r.paymentTerms,
        certifications: r.certifications, expiresOn: r.expiresOn, anonymous: r.anonymous, lang: r.lang, specValues,
      });
    }).catch((e) => setError(errorText(e)));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!f.categoryId) { setFields([]); return; }
    api(`/categories/${f.categoryId}/fields`).then((list) => setFields(list.map((x) => ({ ...x, required: false })))).catch(() => setFields([]));
  }, [f.categoryId]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const on = (k) => (e) => set(k, e.target.value);
  const toggle = (k, v) => setF((x) => ({ ...x, [k]: x[k].includes(v) ? x[k].filter((y) => y !== v) : [...x[k], v] }));
  const chosen = f.categoryId ? tree.get(f.categoryId) : null;
  const limitReached = usage && usage.max !== null && usage.used >= usage.max;
  const unitChoices = UNITS.includes(f.unit) || !f.unit ? UNITS : [f.unit, ...UNITS];

  async function removeFile(fileId) {
    try { setExisting(await api(`/my/buying-requests/${existing.id}/files/${fileId}`, { method: 'DELETE' })); } catch (e) { setError(errorText(e)); }
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    const { specValues, ...rest } = f;
    const body = { ...rest, priceOnRequest: f.priceOnRequest || f.targetPrice === '', catalogSpecs: specsToBody(fields, specValues) };
    try {
      let r = id ? await api(`/my/buying-requests/${id}`, { method: 'PATCH', body }) : await api('/my/buying-requests', { method: 'POST', body });
      if (files.length) {
        const fd = new FormData();
        for (const file of files) fd.append('files', file);
        r = await api(`/my/buying-requests/${r.id}/files`, { method: 'POST', form: fd });
      }
      navigate(`/account/buying-requests/${r.id}`);
    } catch (err) {
      setError(errorText(err));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally { setBusy(false); }
  }

  if (limitReached && !id) {
    return (
      <div className="acc-page">
        <AccountHead title={t('buying.newTitle')} />
        <div className="callout callout-warning"><p>{t('buying.limitReached', { max: usage.max })}</p><Link className="btn btn-primary btn-sm" to="/account/plan">{t('accountNav.plan')}</Link> <Link className="btn btn-ghost btn-sm" to="/account/buying-requests">{t('buying.myTitle')}</Link></div>
      </div>
    );
  }

  return (
    <div className="acc-page">
      <AccountHead title={id ? t('buying.editTitle') : t('buying.newTitle')} lead={t('buying.myLead')} />
      <Alert>{error}</Alert>
      <form className="req-form" onSubmit={submit}>
        <section className="card">
          <Field label={t('buying.f.title')} htmlFor="rq-title"><input id="rq-title" required minLength={3} maxLength={160} value={f.title} placeholder={t('buying.f.titlePh')} onChange={on('title')} /></Field>
          <div className="field">
            <span className="field-label">{t('buying.f.category')} <span className="req" aria-hidden="true">*</span></span>
            {chosen && !picking ? (
              <p className="req-cat-pick"><strong>{tree.pathText(chosen.id, lang)}</strong><button type="button" className="btn btn-link btn-sm" onClick={() => setPicking(true)}>{t('buying.f.changeCategory')}</button></p>
            ) : (
              <>
                <p className="field-hint">{t('buying.f.categoryHint')}</p>
                {chosen && <p className="req-cat-pick"><strong>{tree.pathText(chosen.id, lang)}</strong></p>}
                <CategoryPicker tree={tree} value={f.categoryId} onPick={(cid) => { set('categoryId', String(cid)); setPicking(false); }} />
              </>
            )}
          </div>
          {fields.length > 0 && (
            <div className="field"><span className="field-label">{t('buying.f.specs')} <span className="field-optional">({t('common.optional')})</span></span>
              <SpecFields fields={fields} values={f.specValues} onChange={(v) => set('specValues', v)} />
            </div>
          )}
          <Field label={t('buying.f.description')} optional htmlFor="rq-desc"><RichTextEditor id="rq-desc" value={f.descriptionHtml} onChange={(v) => set('descriptionHtml', v)} placeholder={t('buying.f.descriptionPh')} /></Field>
          <div className="grid2">
            <Field label={t('buying.f.language')} htmlFor="rq-lang"><select id="rq-lang" value={f.lang} onChange={on('lang')}><option value="fr">Français</option><option value="en">English</option></select></Field>
          </div>
          <p className="field-hint">{f.lang === 'en' ? t('buying.f.translateNoteEn') : t('buying.f.translateNote')}</p>
        </section>

        <section className="card">
          <div className="grid2">
            <Field label={t('buying.f.quantity')} htmlFor="rq-qty"><input id="rq-qty" type="number" min="0" step="0.01" inputMode="decimal" required value={f.quantity} onChange={on('quantity')} /></Field>
            <Field label={t('buying.f.unit')} htmlFor="rq-unit"><select id="rq-unit" required value={f.unit} onChange={on('unit')}>{unitChoices.map((u) => <option key={u} value={u}>{unitLabel(u, t)}</option>)}</select></Field>
          </div>
          <label className="checkbox"><input type="checkbox" checked={f.priceOnRequest} onChange={(e) => set('priceOnRequest', e.target.checked)} /><span>{t('buying.f.priceOnRequest')}</span></label>
          {!f.priceOnRequest && (
            <div className="grid2">
              <Field label={t('buying.f.targetPrice')} htmlFor="rq-price"><input id="rq-price" type="number" min="0" step="0.01" inputMode="decimal" value={f.targetPrice} onChange={on('targetPrice')} /></Field>
              <Field label={t('buying.f.currency')} htmlFor="rq-cur"><select id="rq-cur" value={f.currency} onChange={on('currency')}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
            </div>
          )}
          <div className="grid2">
            <Field label={t('buying.f.incoterm')} optional htmlFor="rq-inco"><select id="rq-inco" value={f.incoterm} onChange={on('incoterm')}><option value="">—</option>{INCOTERMS.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label={t('buying.f.destCountry')} optional htmlFor="rq-country"><select id="rq-country" value={f.destCountry} onChange={on('destCountry')}><option value="">—</option>{countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}</select></Field>
            <Field label={t('buying.f.destPort')} optional htmlFor="rq-port"><input id="rq-port" maxLength={120} value={f.destPort} placeholder={t('buying.f.destPortPh')} onChange={on('destPort')} /></Field>
          </div>
          <div className="grid2">
            <Field label={t('buying.f.deliveryBy')} optional htmlFor="rq-by"><input id="rq-by" type="date" min={inDays(0)} value={f.deliveryBy} onChange={on('deliveryBy')} /></Field>
            <Field label={t('buying.f.timeframe')} optional htmlFor="rq-tf"><input id="rq-tf" maxLength={60} value={f.timeframe} placeholder={t('buying.f.timeframePh')} onChange={on('timeframe')} /></Field>
          </div>
          <div className="field" role="group" aria-labelledby="rq-pay-l">
            <span className="field-label" id="rq-pay-l">{t('buying.f.paymentTerms')} <span className="field-optional">({t('common.optional')})</span></span>
            <div className="chip-choice">{PAYMENT.map((c) => <button key={c} type="button" className={`chip-toggle${f.paymentTerms.includes(c) ? ' on' : ''}`} aria-pressed={f.paymentTerms.includes(c)} title={t(`payment.${c}Long`)} onClick={() => toggle('paymentTerms', c)}>{t(`payment.${c}`)}</button>)}</div>
          </div>
          <div className="field" role="group" aria-labelledby="rq-cert-l">
            <span className="field-label" id="rq-cert-l">{t('buying.f.certifications')} <span className="field-optional">({t('common.optional')})</span></span>
            <div className="chip-choice">
              {[...CERTS, ...f.certifications.filter((c) => !CERTS.includes(c))].map((c) => <button key={c} type="button" className={`chip-toggle${f.certifications.includes(c) ? ' on' : ''}`} aria-pressed={f.certifications.includes(c)} onClick={() => toggle('certifications', c)}>{c}</button>)}
            </div>
            <input aria-label={t('buying.f.certifications')} value={certText} maxLength={60} placeholder={t('buying.f.certificationsPh')} onChange={(e) => setCertText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const c = certText.trim().replace(/,/g, ' '); if (c && !f.certifications.includes(c)) set('certifications', [...f.certifications, c]); setCertText(''); } }} />
          </div>
        </section>

        <section className="card">
          <div className="field">
            <span className="field-label">{t('buying.f.files')}</span>
            <p className="field-hint">{t('buying.f.filesHint')}</p>
            {existing && (existing.photos.length > 0 || existing.attachments.length > 0) && (
              <div className="req-files">
                {existing.photos.map((p) => <span key={p.id} className="req-file"><img src={p.url} alt="" /><button type="button" aria-label={t('common.delete')} onClick={() => removeFile(p.id)}>✕</button></span>)}
                {existing.attachments.map((a) => <span key={a.id} className="req-file">📄 <span>{a.name}</span><button type="button" aria-label={t('common.delete')} onClick={() => removeFile(a.id)}>✕</button></span>)}
              </div>
            )}
            <input type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" aria-label={t('buying.f.addFiles')} onChange={(e) => setFiles([...e.target.files].slice(0, 6))} />
          </div>
          <div className="grid2">
            <Field label={t('buying.f.expiresOn')} hint={t('buying.f.expiresHint')} htmlFor="rq-exp"><input id="rq-exp" type="date" required min={inDays(0)} max={inDays(180)} value={f.expiresOn} onChange={on('expiresOn')} /></Field>
          </div>
          <div className="field req-visibility" role="radiogroup" aria-labelledby="rq-vis-l">
            <span className="field-label" id="rq-vis-l">{t('buying.f.visibility')}</span>
            <label className={!f.anonymous ? 'on' : ''}><input type="radio" name="rq-vis" checked={!f.anonymous} onChange={() => set('anonymous', false)} /><span><strong>{t('buying.f.showCompany')}</strong><br /><span className="muted small">{t('buying.f.showCompanyHint')}</span></span></label>
            <label className={f.anonymous ? 'on' : ''}><input type="radio" name="rq-vis" checked={f.anonymous} onChange={() => set('anonymous', true)} /><span><strong>🔒 {t('buying.f.anonymous')}</strong><br /><span className="muted small">{t('buying.f.anonymousHint')}</span></span></label>
          </div>
        </section>

        {usage && <p className="muted small">{usage.max === null ? t('buying.usageNoLimit', { used: usage.used }) : t('buying.usage', { used: usage.used, max: usage.max })}</p>}
        <p className="muted small">{t('buying.f.reviewNote')}</p>
        <div className="req-actions">
          <button type="submit" className="btn btn-primary btn-lg" disabled={busy || !f.categoryId}>{busy ? t('buying.f.saving') : id ? t('buying.f.save') : t('buying.f.submit')}</button>
          <Link className="btn btn-ghost" to={id ? `/account/buying-requests/${id}` : '/account/buying-requests'}>{t('buying.f.cancel')}</Link>
        </div>
      </form>
    </div>
  );
}
